import * as React from 'react';
import {useSelector} from 'react-redux';
import IconButton from "@mui/material/IconButton";
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import {Link as RouterLink, useNavigate} from "react-router-dom";
import Box from "@mui/material/Box";
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import AccountCircle from '@mui/icons-material/AccountCircle';
import OutlinedInput from '@mui/material/OutlinedInput';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import {ReactComponent as UserIcon} from "images/User_Notification.svg"
import {routes} from "routes";
import ContactFormModal from "components/organisms/ContactFormModal/ContactFormModal";
import Button from "@mui/material/Button";
import {useAuth0} from "@auth0/auth0-react";
import Divider from "@mui/material/Divider";
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import CircularProgress from '@mui/material/CircularProgress';

// Simple fuzzy search function
const fuzzySearch = (searchTerm, text) => {
  if (!searchTerm) return false;
  
  const searchLower = searchTerm.toLowerCase();
  const textLower = text.toLowerCase();
  
  let searchIndex = 0;
  for (let i = 0; i < textLower.length; i++) {
    if (textLower[i] === searchLower[searchIndex]) {
      searchIndex++;
      if (searchIndex === searchLower.length) {
        return true;
      }
    }
  }
  return false;
};

export default function UserMenu({mobile}) {
  const { loginWithRedirect, logout } = useAuth0();
  const isAuthorised = useSelector(state => state.global.isAuthorised);
  const strategies = useSelector(state => state.strategy.entities);
  const strategyIds = useSelector(state => state.strategy.ids);
  const isLoading = useSelector(state => state.strategy.loading);
  const profile = useSelector(state => state.userProfile.data);
  const navigate = useNavigate();

  // Search state
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showResults, setShowResults] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState([]);
  const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false);
  const searchInputRef = React.useRef(null);

  // User menu state
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    if (value.trim() === '') {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    // Filter strategies based on search term
    const results = strategyIds
      .map(id => strategies[id])
      .filter(strategy => fuzzySearch(value, strategy.name))
      .slice(0, 5); // Limit to 5 results for better UX
    
    setSearchResults(results);
    setShowResults(true);
  };

  // Handle strategy selection
  const handleStrategySelect = (strategyId) => {
    setSearchTerm('');
    setShowResults(false);
    setMobileSearchOpen(false);
    navigate(routes.strategyDetails.replace(':strategyId', strategyId));
  };

  // Handle click away from search results
  const handleClickAway = () => {
    setShowResults(false);
  };

  // Toggle mobile search drawer
  const toggleMobileSearch = () => {
    setMobileSearchOpen(!mobileSearchOpen);
    if (!mobileSearchOpen) {
      setSearchTerm('');
      setSearchResults([]);
    }
  };

  return (
    <Box sx={{ display: 'flex', p: mobile? 0 : 2 }}>

      {!mobile && (
        <Box sx={{width: "40%", position: 'relative'}}>
          <ClickAwayListener onClickAway={handleClickAway}>
            <Box>
              <FormControl sx={{ m: 1 }} fullWidth variant="outlined" size="small">
                <InputLabel htmlFor="search">Search by strategy name</InputLabel>
                <OutlinedInput
                  id="search"
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => searchTerm.trim() !== '' && setShowResults(true)}
                  ref={searchInputRef}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton edge="end">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Search by strategy name"
                />
              </FormControl>
              
              {/* Search Results Dropdown */}
              {showResults && (
                <Paper 
                  sx={{ 
                    position: 'absolute', 
                    width: '100%', 
                    zIndex: 1000,
                    mt: 0.5,
                    maxHeight: '300px',
                    overflow: 'auto',
                    boxShadow: 3,
                    borderRadius: '4px',
                    ml: 1, // Match the margin of the FormControl
                    mr: 1  // Match the margin of the FormControl
                  }}
                >
                  <List dense sx={{ py: 0 }}>
                    {isLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : searchResults.length > 0 ? (
                      searchResults.map((strategy) => (
                        <ListItem 
                          button 
                          key={strategy.id}
                          onClick={() => handleStrategySelect(strategy.id)}
                          sx={{ 
                            '&:hover': { 
                              backgroundColor: 'rgba(0, 0, 0, 0.04)' 
                            },
                            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                            '&:last-child': {
                              borderBottom: 'none'
                            }
                          }}
                        >
                          <ListItemText 
                            primary={
                              <Typography variant="body1" component="div">
                                {strategy.name}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary" component="div">
                                Min Capital: ${strategy.min_capital}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem>
                        <Typography variant="body2" color="text.secondary">
                          No strategies found
                        </Typography>
                      </ListItem>
                    )}
                  </List>
                </Paper>
              )}
            </Box>
          </ClickAwayListener>
        </Box>
      )}

      <Box sx={{ flexGrow: 1 }} />

      {isAuthorised ?
      <Box>

         {mobile && <Tooltip title="Search">
          <IconButton 
            size="small" 
            sx={{ mr: 1 }} 
            color="inherit"
            onClick={toggleMobileSearch}
          >
             <SearchIcon />
          </IconButton>
        </Tooltip>}

         <ContactFormModal mobile={mobile}/>

        <Tooltip title="Account Settings">
          <IconButton onClick={handleClick} size="small" color="inherit">
            {mobile ?  <AccountCircle />:  <UserIcon width="48px"/>}
          </IconButton>
        </Tooltip>

        <Menu anchorEl={anchorEl} id="account-menu" open={open} onClose={handleClose}  >
          <MenuItem component={RouterLink} to={routes.account} onClick={handleClose}> General Account Settings </MenuItem>
          {profile && profile.user_type === 'ADMIN' && (
            <MenuItem component={RouterLink} to={routes.admin} onClick={handleClose}> Admin Dashboard</MenuItem>
          )}
          <Divider />
          <MenuItem onClick={() => {logout({redirectTo: routes.leaderBoard}) ; handleClose()}} > Log Out </MenuItem>

        </Menu>

        {/* Mobile Search Drawer */}
        {mobile && (
          <Drawer
            anchor="top"
            open={mobileSearchOpen}
            onClose={toggleMobileSearch}
          >
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Search Strategies
                </Typography>
                <IconButton onClick={toggleMobileSearch}>
                  <CloseIcon />
                </IconButton>
              </Box>
              
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel htmlFor="mobile-search">Search by strategy name</InputLabel>
                <OutlinedInput
                  id="mobile-search"
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  autoFocus
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton edge="end">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Search by strategy name"
                />
              </FormControl>
              
              <Box sx={{ mt: 2, maxHeight: '60vh', overflow: 'auto' }}>
                <List>
                  {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : searchResults.length > 0 ? (
                    searchResults.map((strategy) => (
                      <ListItem 
                        button 
                        key={strategy.id}
                        onClick={() => handleStrategySelect(strategy.id)}
                      >
                        <ListItemText 
                          primary={strategy.name} 
                          secondary={`Min Capital: $${strategy.min_capital}`}
                        />
                      </ListItem>
                    ))
                  ) : searchTerm.trim() !== '' ? (
                    <ListItem>
                      <Typography variant="body2" color="text.secondary">
                        No strategies found
                      </Typography>
                    </ListItem>
                  ) : null}
                </List>
              </Box>
            </Box>
          </Drawer>
        )}
      </Box>:

      <Box sx={{mt: 2, mr: 1}}>
          <Button color="secondary" variant="contained" onClick={() => loginWithRedirect()}>Login</Button>&nbsp;&nbsp;
          <Button color="secondary" variant="contained" onClick={() => loginWithRedirect({authorizationParams: { screen_hint: "signup", }})} disabled>Sign Up</Button>
      </Box>
      }

    </Box>
  )
}
